import { useEffect, useMemo, useState } from 'react';
import { Plural, t, Trans } from '@lingui/macro';
import { Box, Button, Divider, Stack, Text } from '@mantine/core';
import { observer } from 'mobx-react-lite';

import { UiCard } from '../../components/ui/ui-card/ui-card';
import { useApiClient } from '../../core/libs/api/api-client.provider';
import { HttpResource } from '../../core/libs/http-resource';
import { AppNotify } from '../../features/app-notify/app-notify';
import { createJsonComponent } from '../../json-components/create-json-component';
import { WagonDataProvider } from '../../json-components/train-data-provider';
import { MyPassenger } from './add-passengers/types';
import { getWagonRequest } from './select-seats/get-wagon.request';
import { TWagon } from './types';

type SelectSeatsProps = {
  wagons?: TWagon[];
  passengers?: MyPassenger[];
  onSubmit?: (seats: { wagonNum: number; seatNum: number }[]) => void;
};
export const SelectSeats = ({
  wagons = [],
  passengers = [],
  onSubmit,
}: SelectSeatsProps) => {
  const [seats, setSeats] = useState<Record<number, number[]> | null>(null);

  const countSelectedSeats = useMemo(
    () => (seats !== null ? Object.values(seats).flat().length : 0),
    [seats]
  );

  const remainingSeatCount = passengers.length - countSelectedSeats;

  const hasAvailableSeats = remainingSeatCount > 0;

  const handleChangeSeats = (changedSeats: {
    wagonNum: number;
    seats: number[];
  }) => {
    setSeats((seats) => {
      const result = { ...seats, [changedSeats.wagonNum]: changedSeats.seats };

      return result;
    });
  };

  const handleSubmit = () => {
    const selectedSeats: { wagonNum: number; seatNum: number }[] = [];

    Object.entries(seats ?? {}).forEach(([wagonNum, seats]) => {
      seats.forEach((seat) => {
        selectedSeats.push({
          wagonNum: Number(wagonNum),
          seatNum: Number(seat),
        });
      });
    });

    console.log({ passengers });

    if (selectedSeats.length === 0) {
      new AppNotify().addMessage({ type: 'error', message: t`Выберите место` });
      return;
    }

    if (selectedSeats.length !== passengers.length) {
      new AppNotify().addMessage({ type: 'error', message: t`Выберите место` });
      return;
    }

    onSubmit && onSubmit(selectedSeats);
  };

  return (
    <Stack spacing="sm">
      <UiCard>
        <Trans>Голова поезда находится в верху экрана</Trans>
        <br />
        <Divider my="sm" />
        {hasAvailableSeats ? (
          <>
            <Plural
              value={remainingSeatCount}
              _0="Места выбраны"
              _1="Осталось выбрать 1 место"
              _2="Осталось выбрать 2 места"
              _3="Осталось выбрать 3 места"
              _4="Осталось выбрать 4 места"
              other=""
            />
          </>
        ) : (
          <span>
            <Trans>Места выбраны</Trans>
          </span>
        )}
      </UiCard>

      {wagons.map((wagon) => (
        <Wagon
          key={wagon.number}
          data={wagon}
          disabled={!hasAvailableSeats}
          onChange={handleChangeSeats}
        />
      ))}

      <UiCard>
        <Button fullWidth size="lg" onClick={handleSubmit}>
          <Trans> Перейти к оформлению</Trans>
        </Button>
      </UiCard>
    </Stack>
  );
};

type WagonProps = {
  data: TWagon;
  disabled?: boolean;
  onChange?: (seats: { wagonNum: number; seats: number[] }) => void;
};
export const Wagon = observer(({ data, disabled, onChange }: WagonProps) => {
  const apiClient = useApiClient();
  const direction = data.direction ?? 'forward';
  console.log({ direction });
  const [wagonSchemeJson, setWagonShemeJson] = useState(null);

  const [getWagon] = useState(
    () =>
      new HttpResource<number, any>({
        apiClient,
        httpRequest: getWagonRequest,
        errorNotify: new AppNotify(),
        onFinish(result: any) {
          setWagonShemeJson(result);
        },
      })
  );
  const handleOnChange = (seats: number[]) => {
    onChange && onChange({ wagonNum: data.number, seats });
  };

  useEffect(() => {
    getWagon.send(data.typeId);
  }, []);

  const title = (
    <Text fw="bold" span>
      <Trans>Вагон</Trans> {data.number}
    </Text>
  );

  return (
    <UiCard title={title}>
      {!getWagon.isWaitingForLoad && (
        <WagonPainter
          disabled={disabled}
          scheme={wagonSchemeJson}
          direction={direction}
          seats={data.seats}
          onChange={handleOnChange}
        />
      )}
    </UiCard>
  );
});

type WagonPainterProps = {
  scheme: any | null;
  direction: 'forward' | 'backward';
  seats: TWagon['seats'];
  disabled?: boolean;
  onChange?: (seats: number[]) => void;
};
const WagonPainter = observer(
  ({
    scheme,
    direction,
    seats,
    disabled = false,
    onChange,
  }: WagonPainterProps) => {
    if (!scheme) return null;

    const WagonComponent = useMemo(() => {
      return () => createJsonComponent(scheme);
    }, [scheme]);

    return (
      <WagonDataProvider
        direction={direction}
        seats={seats}
        disabled={disabled}
        onChange={onChange}
      >
        <Box pt="md">
          <WagonComponent />
        </Box>
      </WagonDataProvider>
    );
  }
);
